import { useState, type FormEvent } from 'react';
import { errorMessage } from '../../shared/api/client';
import type { Person } from '../../shared/api/types';
import { Alert, Button, Field } from '../../shared/ui/components';
import { fieldErrorsFrom, showFieldErrors, type FieldError } from '../../shared/ui/fieldErrors';
import { PersonDetailsFields, readPersonDetails, type PersonDetailsInput } from '../../shared/ui/PersonDetailsFields';
import { INTERESTS } from './shared';

/** Lo que se manda a la API: los mismos datos de una persona (opcionales) más lo propio de la visita. */
export type VisitorFormValues = PersonDetailsInput & {
  email: string | null;
  invitedBy: string | null;
  interests: string[];
  prayerRequest: string | null;
  website?: string | null;
};

/** Datos ya capturados (al editar), en el formato de los campos de persona. */
export interface VisitorFormInitial {
  person?: Partial<Person>;
  email?: string | null;
  invitedBy?: string | null;
  interests?: string[];
  prayerRequest?: string | null;
}

/**
 * Visita: se captura igual que el registro de una persona (nombre, apellidos, nacimiento, dirección…),
 * todo opcional menos el nombre. Si después se hace miembro, estos datos se reutilizan.
 */
export function VisitorForm({ onSubmit, onCancel, submitLabel, initial, publicForm = false }: { onSubmit: (v: VisitorFormValues) => Promise<void>; onCancel?: () => void; submitLabel: string; initial?: VisitorFormInitial; publicForm?: boolean }) {
  const [interests, setInterests] = useState<string[]>(initial?.interests ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const f = new FormData(formEl);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const { details, fields } = readPersonDetails(f, { requireAll: false, requireSurname: false });
    const errors: FieldError[] = [...fields];
    const email = t('email');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errors.push({ name: 'email', message: 'Revisa el correo (por ejemplo: nombre@gmail.com) o déjalo vacío.' });
    if (errors.length) {
      setError(errors.length === 1 ? errors[0]!.message : `Revisa los ${errors.length} campos marcados en rojo.`);
      showFieldErrors(formEl, errors);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        ...details,
        email,
        invitedBy: t('invitedBy'),
        interests,
        prayerRequest: t('prayerRequest'),
        ...(publicForm ? { website: t('website') } : {}),
      });
    } catch (err) {
      setError(errorMessage(err));
      showFieldErrors(formEl, fieldErrorsFrom(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      {error ? <Alert>{error}</Alert> : null}
      <PersonDetailsFields person={initial?.person} idPrefix={publicForm ? 'visita' : 'visitante'} optional />
      <Field label="Correo (opcional)" name="email" type="email" maxLength={254} autoComplete="email" defaultValue={initial?.email ?? ''} />
      <Field label={publicForm ? '¿Quién te invitó? (opcional)' : 'Invitado por (opcional)'} name="invitedBy" maxLength={120} defaultValue={initial?.invitedBy ?? ''} />
      <fieldset className="picker">
        <legend>{publicForm ? '¿Qué te interesa?' : 'Le interesa'}</legend>
        <ul className="picker__list picker__list--grid">
          {INTERESTS.map((i) => (
            <li key={i}>
              <label className="check">
                <input type="checkbox" checked={interests.includes(i)} onChange={() => setInterests((v) => (v.includes(i) ? v.filter((x) => x !== i) : [...v, i]))} /> {i}
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="field">
        <label htmlFor="visitor-prayer">{publicForm ? '¿Podemos orar por algo? (opcional)' : 'Petición de oración (opcional)'}</label>
        <textarea id="visitor-prayer" name="prayerRequest" rows={2} maxLength={2000} defaultValue={initial?.prayerRequest ?? ''} />
      </div>
      {publicForm ? (
        <div className="visually-hidden" aria-hidden="true">
          <label htmlFor="visitor-website">No llenar</label>
          <input id="visitor-website" name="website" tabIndex={-1} autoComplete="off" />
        </div>
      ) : null}
      {onCancel ? (
        <div className="actions">
          <Button type="submit" busy={busy}>
            {submitLabel}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      ) : (
        <Button type="submit" busy={busy}>
          {submitLabel}
        </Button>
      )}
    </form>
  );
}

/** Visita guardada → datos para los campos de persona (también para registrarla como miembro). */
export function visitorAsPerson(v: { givenName: string; familyName: string; phone: string | null; details?: Record<string, unknown> }): Partial<Person> {
  const d = (v.details ?? {}) as Partial<Person> & { paternalSurname?: string | null; maternalSurname?: string | null };
  return {
    givenName: v.givenName,
    paternalSurname: d.paternalSurname ?? v.familyName ?? '',
    maternalSurname: d.maternalSurname ?? '',
    gender: d.gender ?? null,
    birthDate: d.birthDate ?? null,
    birthCity: d.birthCity ?? null,
    birthState: d.birthState ?? null,
    birthCountry: d.birthCountry ?? null,
    address: d.address ?? null,
    baptized: d.baptized ?? null,
    mobilePhone: v.phone,
    homePhone: d.homePhone ?? null,
  } as Partial<Person>;
}
